import { useNewCode } from '@calendly/feature-check-react';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { ArrowExpandIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import { Modal } from '@calendly/ui/components/modal';
import { SimpleSelect } from '@calendly/ui/components/select';
import { HeadingText, BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useState, forwardRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetUserQuery, useUpdateUserGrowthExperimentsMutation } from 'common';
import { MODAL_ACTION, BILLING_ENTRYPOINTS, MODAL_DETAILS, MODAL_NAMES } from 'features/billing/constants';
import { checkIsFreeEmailDomain } from 'features/meeting_intelligence';
import { useTrack, useTrackOnce } from 'hooks/analytics_events/useTrack';
import { useStartNotetakerTrial, NOTETAKER_SCOPE_EXTERNAL, NOTETAKER_SCOPE_ALL, RECAP_EMAIL_RECIPIENTS_ALL, RECAP_EMAIL_RECIPIENTS_ONLY_ME, NOTETAKER_TRIAL_DEFAULT_SETTINGS } from 'hooks/useStartNotetakerTrial';
import RecapImage from './assets/recap.png';
import RecapShapesImage from './assets/recap_shapes.png';

// Styled components
const modalContentIsPhoneCss = "m7fix3c";
const modalContentCss = "m1c8tsl3";
const bodyWrapperIsPhoneCss = "b1ynfto6";
const bodyWrapperCss = "b1oouyqt";
const leftContentIsPhoneCss = "lk8i9in";
const leftContentCss = "l1f2yxcd";
const rightContentCss = "r1s3dblt";
const shapesImageCss = "sb9ehbo";
const imageStyleCss = "i1um91vs";
const configSectionCss = "c1z6p79";
const selectFieldCss = "s1resajo";
const mobileScrollableContentIsPhoneCss = "m11y4vau";
const mobileScrollableContentCss = "m16rfews";
const mobileImageContainerCss = "m12xctuh";
const mobileImageCss = "mlynknv";
const buttonsContainerIsPhoneCss = "b1sfxnz0";
const buttonsContainerCss = "b1bgxw97";
const mobileButtonCss = "m1vtxkda";
const learnMoreLinkCss = "l1o74i1o";
const termsLinkCss = "tjvqohf";
const LearnMoreLink = ({
  children
}) => <Link href="https://help.calendly.com/hc/en-us/articles/21652725311383" target="_blank" rel="noopener noreferrer" underline={false} className={learnMoreLinkCss}>
    {children}
    <ArrowExpandIcon />
  </Link>;
const TermsLink = ({
  children
}) => <Link href="https://calendly.com/terms" target="_blank" rel="noopener noreferrer" underline={false} className={termsLinkCss}>
    {children}
  </Link>;
const ModalContent = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isMobile = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(modalContentCss, {
    [modalContentIsPhoneCss]: isMobile
  }, props.className)} />;
});
const BodyWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isMobile = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(bodyWrapperCss, {
    [bodyWrapperIsPhoneCss]: isMobile
  }, props.className)} />;
});
const LeftContent = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isMobile = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(leftContentCss, {
    [leftContentIsPhoneCss]: isMobile
  }, props.className)} />;
});
const MobileScrollableContent = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isMobile = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(mobileScrollableContentCss, {
    [mobileScrollableContentIsPhoneCss]: isMobile
  }, props.className)} />;
});
const ButtonsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isMobile = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(buttonsContainerCss, {
    [buttonsContainerIsPhoneCss]: isMobile
  }, props.className)} />;
});
const RightContent = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(rightContentCss, props.className)} />;
});
const MobileImageContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(mobileImageContainerCss, props.className)} />;
});
const ConfigSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(configSectionCss, props.className)} />;
});
const SelectField = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(selectFieldCss, props.className)} />;
});
export const NotetakerOptInModal = () => {
  const isMobile = useDeviceType('isPhone');
  const isAutomatedRecapSharingEnabled = useNewCode('automated_recap_sharing')?.enabled;
  const [isOpen, setIsOpen] = useState(true);
  const [notetakerScope, setNotetakerScope] = useState(NOTETAKER_TRIAL_DEFAULT_SETTINGS.NOTETAKER_SCOPE);
  const [recapEmailRecipients, setRecapEmailRecipients] = useState(NOTETAKER_TRIAL_DEFAULT_SETTINGS.RECAP_EMAIL_RECIPIENTS);
  const {
    pathname
  } = useLocation();
  const {
    data: userData
  } = useGetUserQuery();
  const isFreeEmail = checkIsFreeEmailDomain(userData?.email);
  const [updateGrowthExperiments] = useUpdateUserGrowthExperimentsMutation();
  const track = useTrack();
  const {
    startTrial,
    isLoading
  } = useStartNotetakerTrial({
    onSuccess: () => {
      track({
        name: 'Modal Interaction',
        properties: {
          action: MODAL_ACTION.START_TRIAL,
          modal_name: MODAL_NAMES.NOTETAKER_OPT_IN,
          modal_details: MODAL_DETAILS.NOTETAKER_OPT_IN,
          location: BILLING_ENTRYPOINTS.NOTETAKER_TRIAL_OPT_IN_MODAL,
          path: pathname
        }
      });
    }
  });
  const handleClose = () => {
    updateGrowthExperiments({
      seen_notetaker_opt_in_modal_date: new Date().toISOString()
    });
    track({
      name: 'Modal Interaction',
      properties: {
        action: MODAL_ACTION.DISMISSED,
        modal_name: MODAL_NAMES.NOTETAKER_OPT_IN,
        modal_details: MODAL_DETAILS.NOTETAKER_OPT_IN,
        location: BILLING_ENTRYPOINTS.NOTETAKER_TRIAL_OPT_IN_MODAL,
        path: pathname
      }
    });
    setIsOpen(false);
  };
  const handleStartTrial = () => {
    startTrial({
      notetakerScope,
      recapEmailRecipients
    });
  };
  useTrackOnce(true, {
    name: 'Modal Interaction',
    properties: {
      action: MODAL_ACTION.OPENING,
      modal_name: MODAL_NAMES.NOTETAKER_OPT_IN,
      modal_details: MODAL_DETAILS.NOTETAKER_OPT_IN,
      location: BILLING_ENTRYPOINTS.NOTETAKER_TRIAL_OPT_IN_MODAL,
      path: pathname
    }
  });
  return <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalContent>
        <BodyWrapper>
          <LeftContent>
            <MobileScrollableContent>
              <HeadingText level={2}>
                {I18n.t('publisher.notetaker_opt_in.title')}
              </HeadingText>
              <BodyText>
                {HtmlParser(I18n.t('publisher.notetaker_opt_in.description'), {
                LearnMoreLink
              })}
              </BodyText>

              {isMobile && <MobileImageContainer>
                  <img src={RecapImage} alt={I18n.t('publisher.notetaker_opt_in.image_alt')} className={mobileImageCss} />
                </MobileImageContainer>}

              <ConfigSection>
                {!isFreeEmail && <SelectField>
                    <BodyText weight="bold">
                      {I18n.t('publisher.notetaker_opt_in.meeting_scope_label')}
                    </BodyText>
                    <SimpleSelect value={notetakerScope} onChange={setNotetakerScope} size="small" options={[{
                  name: I18n.t('publisher.notetaker_opt_in.scope_options.external'),
                  value: NOTETAKER_SCOPE_EXTERNAL
                }, {
                  name: I18n.t('publisher.notetaker_opt_in.scope_options.all'),
                  value: NOTETAKER_SCOPE_ALL
                }]} />
                  </SelectField>}

                {isAutomatedRecapSharingEnabled && <SelectField>
                    <BodyText weight="bold">
                      {I18n.t('publisher.notetaker_opt_in.email_recipients_label')}
                    </BodyText>
                    <SimpleSelect value={recapEmailRecipients} onChange={setRecapEmailRecipients} size="small" options={[{
                  name: I18n.t('publisher.notetaker_opt_in.recap_email_recipients_options.all'),
                  value: RECAP_EMAIL_RECIPIENTS_ALL
                }, {
                  name: I18n.t('publisher.notetaker_opt_in.recap_email_recipients_options.only_me'),
                  value: RECAP_EMAIL_RECIPIENTS_ONLY_ME
                }]} />
                  </SelectField>}
              </ConfigSection>

              <BodyText size="small" color="muted">
                {HtmlParser(I18n.t('publisher.notetaker_opt_in.footer'), {
                TermsLink
              })}
              </BodyText>
            </MobileScrollableContent>

            <ButtonsContainer>
              <Button decoration="secondary-outline" onClick={handleClose} disabled={isLoading} className={isMobile ? mobileButtonCss : undefined}>
                {I18n.t('publisher.notetaker_opt_in.no_thanks')}
              </Button>
              <Button onClick={handleStartTrial} loading={isLoading} disabled={isLoading} className={isMobile ? mobileButtonCss : undefined}>
                {I18n.t('publisher.notetaker_opt_in.start_trial')}
              </Button>
            </ButtonsContainer>
          </LeftContent>

          {!isMobile && <RightContent>
              <img src={RecapImage} alt={I18n.t('publisher.notetaker_opt_in.image_alt')} className={imageStyleCss} />
              <img src={RecapShapesImage} alt="" className={shapesImageCss} />
            </RightContent>}
        </BodyWrapper>
      </ModalContent>
    </Modal>;
};

require("./notetaker_opt_in_modal.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./notetaker_opt_in_modal.js");